<template>
  <div class="dsc-outer">
    <!-- Toggle lives outside the canvas so it doesn't block clicks -->
    <div class="dsc-toolbar">
      <button
        class="dsc-toggle"
        :class="{ active: excludeBills }"
        @click="excludeBills = !excludeBills"
      >
        <i class="fas" :class="excludeBills ? 'fa-check-square' : 'fa-square'"></i>
        Exclude fixed bills
      </button>
      <span v-if="excludeBills" class="dsc-hint">Rent, utilities, subscriptions &amp; similar</span>
    </div>

    <div class="dsc-canvas-wrap">
      <canvas ref="canvas"></canvas>
      <div v-if="isEmpty" class="dsc-empty">No expense data</div>
    </div>
  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Categories considered fixed/non-discretionary — filtered when toggle is on
const BILL_CATEGORIES = new Set([
  'rent', 'car_payment', 'car_insurance', 'utilities',
  'subscriptions', 'gym_memberships', 'steep_hollow',
]);

function daysInMonth(year, month) { return new Date(year, month + 1, 0).getDate(); }

function dailyTotals(expenses, year, month) {
  const days = daysInMonth(year, month);
  const out  = new Array(days).fill(0);
  expenses.forEach(t => {
    if (!t.v3_date) return;
    const d = parseInt(t.v3_date.split('-')[2], 10) - 1;
    if (d >= 0 && d < days) out[d] += Math.abs(t.v3_amount || 0);
  });
  return out;
}

function monthLabel(year, month) { return MONTH_NAMES[month] + ' ' + year; }

function pad(arr, len) {
  const out = [...arr];
  while (out.length < len) out.push(null);
  return out;
}

export default {
  name: 'DailySpendChart',
  props: {
    expenses:              { type: Array,  default: () => [] },
    selectedYear:          { type: Number, required: true },
    selectedMonth:         { type: Number, required: true },
    prevMonthExpenses:     { type: Array,  default: () => [] },
    prevMonthYear:         { type: Number, default: null },
    prevMonth:             { type: Number, default: null },
    prevPrevMonthExpenses: { type: Array,  default: () => [] },
    prevPrevMonthYear:     { type: Number, default: null },
    prevPrevMonth:         { type: Number, default: null },
  },
  data() { return { chart: null, renderTimer: null, excludeBills: false }; },
  computed: {
    isEmpty() { return this.expenses.length === 0; },
    maxDays() {
      return Math.max(
        daysInMonth(this.selectedYear, this.selectedMonth),
        this.prevMonth     != null ? daysInMonth(this.prevMonthYear,     this.prevMonth)     : 0,
        this.prevPrevMonth != null ? daysInMonth(this.prevPrevMonthYear, this.prevPrevMonth) : 0,
      );
    },
    todayMarker() {
      const n = new Date();
      return n.getMonth() === this.selectedMonth && n.getFullYear() === this.selectedYear
        ? n.getDate() - 1 : null;
    },
  },
  methods: {
    // Strip bill categories when toggle is active — applied to all series
    // so the comparison stays apples-to-apples.
    filterExpenses(arr) {
      if (!this.excludeBills) return arr;
      return arr.filter(t => !BILL_CATEGORIES.has(t.v3_category));
    },
    scheduleRender() {
      if (this.renderTimer) clearTimeout(this.renderTimer);
      this.renderTimer = setTimeout(() => { this.renderTimer = null; this.buildChart(); }, 150);
    },
    buildChart() {
      const canvas = this.$refs.canvas;
      if (!canvas || canvas.offsetWidth === 0) { this.scheduleRender(); return; }
      if (this.isEmpty) return;

      if (this.chart) {
        try { this.chart.destroy(); } catch (e) { /* ignore */ }
        this.chart = null;
        requestAnimationFrame(() => {
          if (this.$refs.canvas) this.buildChart();
        });
        return;
      }

      if (!canvas.getContext('2d')) { this.scheduleRender(); return; }

      const len    = this.maxDays;
      const labels = Array.from({ length: len }, (_, i) => i + 1);

      const current  = pad(dailyTotals(this.filterExpenses(this.expenses),              this.selectedYear,     this.selectedMonth),  len);
      const prev     = this.prevMonth     != null ? pad(dailyTotals(this.filterExpenses(this.prevMonthExpenses),     this.prevMonthYear,     this.prevMonth),     len) : null;
      const prevPrev = this.prevPrevMonth != null ? pad(dailyTotals(this.filterExpenses(this.prevPrevMonthExpenses), this.prevPrevMonthYear, this.prevPrevMonth), len) : null;

      const todayIdx = this.todayMarker;
      const datasets = [];

      // Current month — bars
      datasets.push({
        type: 'bar',
        label: monthLabel(this.selectedYear, this.selectedMonth),
        data: current,
        backgroundColor: 'rgba(79,142,247,0.65)',
        borderColor:     'rgba(79,142,247,1)',
        borderWidth: 1,
        borderRadius: 3,
        order: 3,
      });

      // Last month — solid line
      if (prev) {
        datasets.push({
          type: 'line',
          label: monthLabel(this.prevMonthYear, this.prevMonth),
          data: prev,
          borderColor:     'rgba(251,146,60,0.85)',
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: 0, pointHoverRadius: 4,
          tension: 0.35, order: 2, spanGaps: true,
        });
      }

      // Two months ago — dashed line
      if (prevPrev) {
        datasets.push({
          type: 'line',
          label: monthLabel(this.prevPrevMonthYear, this.prevPrevMonth),
          data: prevPrev,
          borderColor:     'rgba(156,163,175,0.75)',
          backgroundColor: 'transparent',
          borderWidth: 1.5,
          borderDash: [5, 4],
          pointRadius: 0, pointHoverRadius: 4,
          tension: 0.35, order: 1, spanGaps: true,
        });
      }

      this.chart = new Chart(canvas, {
        type: 'bar',
        data: { labels, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: {
              position: 'top',
              labels: {
                usePointStyle: true, padding: 14, font: { size: 12 },
                generateLabels: chart => chart.data.datasets.map((ds, i) => ({
                  text: ds.label,
                  fillStyle:   ds.type === 'bar' ? ds.backgroundColor : ds.borderColor,
                  strokeStyle: ds.borderColor,
                  lineWidth:   ds.borderWidth,
                  lineDash:    ds.borderDash || [],
                  pointStyle:  ds.type === 'bar' ? 'rect' : 'line',
                  hidden:      !chart.isDatasetVisible(i),
                  datasetIndex: i,
                })),
              },
            },
            tooltip: {
              callbacks: {
                title: items => `Day ${items[0].label}`,
                label: ctx => {
                  const v = ctx.parsed.y;
                  if (!v) return null;
                  return ` ${ctx.dataset.label}: $${Math.round(v).toLocaleString()}`;
                },
              },
            },
          },
          scales: {
            x: {
              grid: { display: false }, border: { display: false },
              ticks: {
                font: { size: 11 }, color: '#9ca3af', maxRotation: 0,
                callback: (_, i) => { const d = i + 1; return d === 1 || d % 5 === 0 ? d : ''; },
              },
            },
            y: {
              border: { display: false },
              grid: { color: 'rgba(0,0,0,0.05)' },
              ticks: { font: { size: 11 }, color: '#9ca3af', callback: v => '$' + Math.round(v).toLocaleString() },
            },
          },
        },
        plugins: [{
          id: 'todayLine',
          afterDraw(chart) {
            if (todayIdx == null) return;
            const bar = chart.getDatasetMeta(0)?.data?.[todayIdx];
            if (!bar) return;
            const { ctx, chartArea: { top, bottom } } = chart;
            ctx.save();
            ctx.setLineDash([3, 3]);
            ctx.strokeStyle = 'rgba(79,142,247,0.4)';
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.moveTo(bar.x, top);
            ctx.lineTo(bar.x, bottom);
            ctx.stroke();
            ctx.restore();
          },
        }],
      });
    },
  },
  watch: {
    expenses:              { handler() { this.scheduleRender(); }, deep: true },
    prevMonthExpenses:     { handler() { this.scheduleRender(); }, deep: true },
    prevPrevMonthExpenses: { handler() { this.scheduleRender(); }, deep: true },
    excludeBills()         { this.scheduleRender(); },
  },
  mounted() { this.scheduleRender(); },
  beforeUnmount() {
    if (this.renderTimer) clearTimeout(this.renderTimer);
    if (this.chart) { this.chart.destroy(); this.chart = null; }
  },
};
</script>

<style scoped>
.dsc-outer { display: flex; flex-direction: column; gap: 8px; }

.dsc-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.dsc-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border: 1.5px solid #d1d5db;
  border-radius: 99px;
  background: #fff;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s;
}
.dsc-toggle:hover { border-color: #4f8ef7; color: #4f8ef7; }
.dsc-toggle.active {
  border-color: #4338ca;
  background: #eef2ff;
  color: #4338ca;
}
.dsc-hint {
  font-size: 11px;
  color: #9ca3af;
  font-style: italic;
}

.dsc-canvas-wrap {
  position: relative;
  height: 240px;
}
.dsc-empty {
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; color: #9ca3af;
}

@media (max-width: 600px) { .dsc-canvas-wrap { height: 200px; } }
</style>
